import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  eventAt,
  GUIDE_DEFAULT_LANGUAGE,
  type ChannelGuide,
  type GuideEvent,
  type GuideGap,
  type InvalidGuideEvent,
} from "@shared/epg-guide";
import { foyerApi, type ChannelSchedule } from "../../http/foyer";
import { epgErrorText } from "./findings";
import { SectionHead, SoftChip } from "./editorBits";
import EpgEventDetailDialog from "./EpgEventDetailDialog";
import { clock, day, span, stamp } from "./scheduleFormat";
import { tokens } from "../../theme";

/**
 * A channel's schedule, where the mapping that produces it is set (`BE-27`).
 *
 * ## Why this is a panel in an editor and not a guide page
 *
 * Nobody authors EPG. It is read-only data ingested from Simply TV three times
 * a day and Foyer will never write a row of it, so the only job a CMS schedule
 * surface has is **diagnosis**: did this channel's mapping actually produce a
 * schedule, and does it look right? That question belongs next to the thing
 * that decides the answer - the channel's `epg-simply` `alternativeIds` entry
 * (`BE-22`) - and not on a separate page an operator has to think to visit.
 *
 * ## Three things this panel is careful about
 *
 * 1. **"On now" is computed here, from this browser's clock.** The server ships
 *    no `live` flag on purpose: the read behind it is cached in ten-minute bins
 *    (see `api/distributionEpg.ts`), so a baked flag would be wrong within
 *    seconds of being cached. {@link eventAt} is the one function every surface
 *    calls, so a TV guide and this panel cannot disagree at a junction.
 *
 * 2. **A hole is drawn, not left blank.** An unmapped channel and a channel
 *    with a genuinely empty guide are the SAME answer upstream - `200` with no
 *    rows, because `GET /channels/:assetId/epgevents` never checks the channel
 *    exists. So `gaps[]` is rendered as visible missing time, and a window-wide
 *    gap is the strongest signal this surface has that the mapping is wrong.
 *
 * 3. **A dead catalogue is an error with a Retry, never an empty schedule.**
 *    Same rule `BE-15` applied to the lists: "the catalogue is down" must not
 *    read as "this channel has no guide".
 *
 * ## Why there is no week picker
 *
 * Ingest keeps only `[yesterday 00:00, dayAfterTomorrow 00:00)`, so the data
 * ends about two days out. A control offering a week would be a lie the UI
 * tells about data that does not exist. `EPG_MAX_WINDOW_DAYS = 14` is a
 * bug-catching ceiling, not a product horizon.
 */

/** The default window. Short, because the data only reaches ~2 days out. */
export const SCHEDULE_WINDOW_HOURS = 24;

/** How often "on now" is recomputed. */
const TICK_MS = 30_000;

/* ------------------------------------------------------------------ *
 * The timeline
 * ------------------------------------------------------------------ */

export type TimelineEntry =
  | { kind: "event"; at: number; event: GuideEvent }
  | { kind: "gap"; at: number; gap: GuideGap };

/**
 * Events and gaps, interleaved in one chronological list.
 *
 * Merged rather than drawn as two lists because a hole is only legible in the
 * place it happens: "no schedule between 03:00 and 06:00" underneath the 03:00
 * programme says something a separate "Gaps" box never can.
 *
 * A gap ties with the event that follows it only if it is zero-length, which
 * the server has already filtered out (`GUIDE_GAP_MIN_SECONDS`); the tie-break
 * puts the gap first anyway so the order is total.
 */
export function scheduleTimeline(
  events: readonly GuideEvent[],
  gaps: readonly GuideGap[]
): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...events.map(
      (event): TimelineEntry => ({
        kind: "event",
        at: Date.parse(event.startScheduled),
        event,
      })
    ),
    ...gaps.map(
      (gap): TimelineEntry => ({ kind: "gap", at: Date.parse(gap.from), gap })
    ),
  ];
  const rank = (entry: TimelineEntry) => (entry.kind === "gap" ? 0 : 1);
  return entries.sort((a, b) => a.at - b.at || rank(a) - rank(b));
}

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

function ago(seconds: number): string {
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} h ago`;
}

/* ------------------------------------------------------------------ *
 * Rows
 * ------------------------------------------------------------------ */

function EventRow({
  event,
  live,
  guideLanguage,
  onOpen,
}: {
  event: GuideEvent;
  live: boolean;
  /** The language the panel asked for (`data.language`) - compared against
   *  `event.titleLanguage` to tell "this is the language you asked for" from
   *  "ingest never wrote that language, this is a fallback". */
  guideLanguage: string;
  /** Opens the artwork/metadata detail for this event. Every list of this
   *  kind in the CMS opens on the row (see `distribution-cms-surface`'s "a
   *  row opens the asset"), so a schedule row does the same rather than
   *  hiding it behind a menu nobody thinks to open. */
  onOpen: () => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      data-testid={`schedule-event-${event.epgEventId}`}
      data-live={live ? "true" : undefined}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: live ? tokens.brandTintSoft : "transparent",
        cursor: "pointer",
        "&:hover": { bgcolor: tokens.brandTintSoft },
      }}
    >
      <Box sx={{ flex: "0 0 auto", width: 96 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: live ? tokens.brand : tokens.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {clock(event.startScheduled)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
          {span(event.durationSeconds)}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink }}
            data-testid={`schedule-title-${event.epgEventId}`}
          >
            {event.title || "(untitled programme)"}
          </Typography>
          {live && (
            <Box
              data-testid="schedule-on-now"
              sx={{
                px: 1,
                py: 0.125,
                borderRadius: 999,
                bgcolor: tokens.brand,
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.4,
              }}
            >
              ON NOW
            </Box>
          )}
        </Stack>
        {event.synopsis && (
          <Typography
            sx={{
              fontSize: 12,
              color: tokens.inkMuted,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {event.synopsis}
          </Typography>
        )}
        {/* Which language answered, or that nothing did and this is the
            programme's own original title. Cheap, and it is the difference
            between "the guide is in the wrong language" and "there is no
            localised row for it". Two distinct cases, worded differently on
            purpose: a same-asset fallback to another ingested language still
            means ingest wrote real text, which "no localised row" would deny. */}
        {event.titleLanguage === null && event.title && (
          <Typography sx={{ fontSize: 11, color: tokens.inkFaint }}>
            Original title — no localised row in the guide’s language.
          </Typography>
        )}
        {event.titleLanguage !== null &&
          event.titleLanguage !== guideLanguage && (
            <Typography sx={{ fontSize: 11, color: tokens.inkFaint }}>
              Shown in {event.titleLanguage.toUpperCase()} — ingest never wrote
              a {guideLanguage.toUpperCase()} row for this programme.
            </Typography>
          )}
      </Box>
      <Typography
        sx={{
          flex: "0 0 auto",
          fontSize: 11,
          color: tokens.inkFaint,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clock(event.endScheduled)}
      </Typography>
    </Stack>
  );
}

function GapRow({ gap }: { gap: GuideGap }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      data-testid="schedule-gap"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: `1px dashed ${tokens.warning}`,
        bgcolor: tokens.warningTint,
      }}
    >
      <Typography
        sx={{
          flex: "0 0 auto",
          width: 96,
          fontSize: 12,
          fontWeight: 700,
          color: tokens.warning,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clock(gap.from)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: tokens.ink }}>
        No schedule for {span(gap.seconds)} — nothing covers {clock(gap.from)}–
        {clock(gap.to)}.
      </Typography>
    </Stack>
  );
}

function InvalidRow({ row }: { row: InvalidGuideEvent }) {
  const why =
    row.reason === "not_after_start"
      ? "ends at or before it starts"
      : "carries a timestamp that is not a time";
  return (
    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
      • Event #{row.epgEventId} {why} ({String(row.startScheduled)} →{" "}
      {String(row.endScheduled)}).
    </Typography>
  );
}

/* ------------------------------------------------------------------ *
 * The panel
 * ------------------------------------------------------------------ */

function nextWindow(): { from: string; to: string } {
  const now = Date.now();
  return {
    from: new Date(now).toISOString(),
    to: new Date(now + SCHEDULE_WINDOW_HOURS * 3_600_000).toISOString(),
  };
}

export interface ChannelSchedulePreviewProps {
  /** The channel's `assetId`. A channel being created has none, so the caller
   *  simply does not render this - there is nothing to read yet. */
  assetId: string;
}

export default function ChannelSchedulePreview({
  assetId,
}: ChannelSchedulePreviewProps) {
  const [range, setRange] = useState(nextWindow);
  const [data, setData] = useState<ChannelSchedule | null>(null);
  const [readAt, setReadAt] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [detailEvent, setDetailEvent] = useState<GuideEvent | null>(null);

  /** Ticks so "on now" follows the clock rather than the fetch. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(
    async (window: { from: string; to: string }) => {
      setLoading(true);
      setError(null);
      try {
        const answer = await foyerApi.getChannelSchedule(assetId, window);
        setData(answer);
        setReadAt(Date.now());
      } catch (e) {
        const err = e as {
          code?: string;
          message: string;
          body?: { detail?: string };
        };
        // Never an empty schedule on failure. A 502 here means the catalogue is
        // unwell, and drawing "no programmes" would blame the channel for it.
        setData(null);
        setError(
          epgErrorText({
            code: err.code,
            detail: err.body?.detail,
            message: err.message,
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [assetId]
  );

  useEffect(() => {
    void load(range);
  }, [load, range]);

  const guide: ChannelGuide | null = data?.channels?.[0] ?? null;
  const timeline = useMemo(
    () => (guide ? scheduleTimeline(guide.events, guide.gaps) : []),
    [guide]
  );
  const onNow = useMemo(
    () => (guide ? eventAt(guide.events, now) : null),
    [guide, now]
  );

  /** The server's age at the moment it answered, plus how long ago that was. */
  const freshness = data
    ? ago(data.cache.ageSeconds + Math.max(0, (now - readAt) / 1000))
    : null;

  return (
    <Box data-testid="channel-schedule">
      <SectionHead
        title="Programme guide"
        subtitle="Read-only. Ingested from Simply TV three times a day and joined to this channel by its epg-simply mapping — Foyer never writes a schedule."
        aside={
          <>
            {freshness && (
              <SoftChip
                label={`Read ${freshness}`}
                title={`Cached in ${Math.round(
                  (data?.cache.binSeconds ?? 0) / 60
                )}-minute bins, so two operators a few minutes apart share one upstream read.`}
              />
            )}
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={() => setRange(nextWindow())}
              disabled={loading}
              data-testid="schedule-refresh"
              sx={{ minHeight: 0, fontSize: 12, fontWeight: 600 }}
            >
              Refresh
            </Button>
          </>
        }
      />

      {/* Both instants, always stated. This data set punishes "today": ingest
          computes its horizon from a server-local midnight in a UTC container,
          so the only honest window is the two moments the request named. */}
      <Typography
        sx={{ fontSize: 12, color: tokens.inkMuted, mb: 1.5 }}
        data-testid="schedule-window"
      >
        Next {SCHEDULE_WINDOW_HOURS} hours · {stamp(range.from)} →{" "}
        {stamp(range.to)}
        {data ? ` · titles in ${data.language.toUpperCase()}` : ""}
      </Typography>

      {error && (
        <Alert
          severity="error"
          data-testid="schedule-error"
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void load(range)}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && !data && !error && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {guide && !error && (
        <>
          {guide.events.length === 0 ? (
            /* The whole point of the panel. Upstream answers 200 with no rows
               for a channel that is not mapped AND for one that simply has no
               schedule, so this says both out loud rather than drawing nothing
               and letting the operator guess which it was. */
            <Alert severity="warning" data-testid="schedule-empty">
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                No programmes in this window.
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Nothing covers{" "}
                {guide.gaps.length > 0
                  ? span(guide.gaps[0].seconds)
                  : `${SCHEDULE_WINDOW_HOURS} h`}{" "}
                from {stamp(range.from)}. The catalogue answers the same way for
                a channel with no schedule and for one whose epg-simply mapping
                does not match anything, so this cannot tell you which it is.
                Check the channel’s EPG mapping first; ingest runs at 04:00,
                10:00 and 16:00 and keeps roughly yesterday through the day
                after tomorrow.
              </Typography>
            </Alert>
          ) : (
            <>
              <Typography
                sx={{ fontSize: 12, color: tokens.inkMuted, mb: 1 }}
                data-testid="schedule-summary"
              >
                {guide.events.length} programmes
                {guide.gaps.length > 0
                  ? ` · ${guide.gaps.length} gap${
                      guide.gaps.length === 1 ? "" : "s"
                    }`
                  : " · no gaps"}
                {onNow
                  ? ` · on now: ${onNow.title}`
                  : " · nothing on right now"}
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: 2,
                  maxHeight: 460,
                  overflowY: "auto",
                  p: 1,
                }}
              >
                <Stack spacing={0.5}>
                  {timeline.map((entry, index) => {
                    const previous = timeline[index - 1];
                    const iso =
                      entry.kind === "event"
                        ? entry.event.startScheduled
                        : entry.gap.from;
                    const newDay =
                      index === 0 ||
                      (previous &&
                        day(
                          previous.kind === "event"
                            ? previous.event.startScheduled
                            : previous.gap.from
                        ) !== day(iso));
                    return (
                      <Box
                        key={
                          entry.kind === "event"
                            ? `e${entry.event.epgEventId}`
                            : `g${entry.gap.from}`
                        }
                      >
                        {newDay && (
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: tokens.inkFaint,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              px: 1.5,
                              pt: index === 0 ? 0.5 : 1.5,
                              pb: 0.5,
                            }}
                          >
                            {day(iso)}
                          </Typography>
                        )}
                        {entry.kind === "event" ? (
                          <EventRow
                            event={entry.event}
                            live={onNow?.epgEventId === entry.event.epgEventId}
                            guideLanguage={
                              data?.language ?? GUIDE_DEFAULT_LANGUAGE
                            }
                            onOpen={() => setDetailEvent(entry.event)}
                          />
                        ) : (
                          <GapRow gap={entry.gap} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}

          {/* Normal data, not corruption: an EPG event is immutable upstream,
              so a rescheduled programme is a SECOND row rather than an edit,
              and the older one survives until the purge. Out of the main flow,
              because it is noise - but reachable, because an operator chasing
              "the guide showed the wrong film" needs to see it. */}
          {guide.superseded.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                onClick={() => setShowSuperseded((v) => !v)}
                endIcon={
                  showSuperseded ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )
                }
                data-testid="schedule-superseded-toggle"
                sx={{ minHeight: 0, px: 0, fontSize: 12, fontWeight: 600 }}
              >
                {guide.superseded.length} replaced airing
                {guide.superseded.length === 1 ? "" : "s"}
              </Button>
              <Collapse in={showSuperseded}>
                <Typography
                  sx={{ fontSize: 11, color: tokens.inkMuted, mb: 1 }}
                >
                  An event is never updated upstream, so a rescheduled programme
                  arrives as a second row and the older one survives until it is
                  purged. The most recently ingested row wins the slot; these
                  lost it.
                </Typography>
                <Stack spacing={0.5} data-testid="schedule-superseded">
                  {guide.superseded.map((row) => (
                    <Typography
                      key={row.epgEventId}
                      sx={{ fontSize: 12, color: tokens.inkMuted }}
                    >
                      • {stamp(row.startScheduled)}–{clock(row.endScheduled)}{" "}
                      {row.title || "(untitled)"} — replaced by event #
                      {row.supersededBy} (this row was ingested{" "}
                      {stamp(row.ingestedAt)}).
                    </Typography>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )}

          {guide.invalid.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              data-testid="schedule-invalid"
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                {guide.invalid.length} event
                {guide.invalid.length === 1 ? "" : "s"} could not be placed on
                the timeline.
              </Typography>
              <Stack spacing={0.25}>
                {guide.invalid.map((row) => (
                  <InvalidRow key={row.epgEventId} row={row} />
                ))}
              </Stack>
            </Alert>
          )}

          {/* Should always be empty - upstream filters by channel. It is drawn
              when it is not, because this API has already been caught twice
              answering for a row the path did not ask about. */}
          {data && data.foreignChannelAssetIds.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              data-testid="schedule-foreign"
            >
              The catalogue also returned events for{" "}
              {data.foreignChannelAssetIds.join(", ")}, which this request did
              not ask about. They are not shown here.
            </Alert>
          )}
        </>
      )}

      {detailEvent && (
        <EpgEventDetailDialog
          channelAssetId={detailEvent.channelAssetId}
          fallback={detailEvent}
          language={data?.language ?? GUIDE_DEFAULT_LANGUAGE}
          onClose={() => setDetailEvent(null)}
        />
      )}
    </Box>
  );
}
